import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import VideoGenerator from '../components/video/VideoGenerator';
import api from '../utils/api';

const VideoPage = () => {
  const location = useLocation();
  const [videos, setVideos] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentVideoId, setCurrentVideoId] = useState(null);
  const [generationStatus, setGenerationStatus] = useState(null);
  
  // Check if coming from image with suggested prompt
  const fromImage = location.state?.fromImage;
  const sourceImageUrl = location.state?.imageUrl;
  const suggestedPrompt = location.state?.suggestedPrompt;

  // Fetch all videos
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await api.get('/api/videos');
        setVideos(response.data.map(vid => ({
          ...vid,
          thumbnailUrl: vid.thumbnail_url || vid.thumbnailUrl,
          videoUrl: vid.video_url || vid.videoUrl
        })));
      } catch (error) {
        console.error('Failed to fetch videos:', error);
      }
    };
    fetchVideos();
  }, []);

  // Poll for video status when generating
  const pollVideoStatus = useCallback(async (videoId) => {
    try {
      const response = await api.get(`/api/videos/${videoId}`);
      
      const video = response.data;
      setGenerationStatus(video.status);
      
      if (video.status === 'completed') {
        // Video is ready
        setVideos(prev => {
          const existing = prev.find(v => v.id === videoId);
          if (existing) {
            return prev.map(v => v.id === videoId ? {
              ...v,
              status: 'completed',
              videoUrl: video.video_url,
              thumbnailUrl: video.thumbnail_url
            } : v);
          }
          return [{
            id: video.id,
            prompt: video.prompt,
            status: 'completed',
            videoUrl: video.video_url,
            thumbnailUrl: video.thumbnail_url,
            duration: video.duration,
            createdAt: video.created_at
          }, ...prev];
        });
        setIsGenerating(false);
        setCurrentVideoId(null);
        setGenerationStatus(null);
        return true; // Stop polling
      } else if (video.status === 'failed') {
        // Generation failed
        setIsGenerating(false);
        setCurrentVideoId(null);
        setGenerationStatus(null);
        alert(`Video generation failed: ${video.error_message || 'Unknown error'}`);
        return true; // Stop polling
      }
      
      return false; // Continue polling
    } catch (error) {
      console.error('Failed to check video status:', error);
      return false;
    }
  }, []);

  // Polling effect
  useEffect(() => {
    if (!currentVideoId || !isGenerating) return;
    
    const pollInterval = setInterval(async () => {
      const shouldStop = await pollVideoStatus(currentVideoId);
      if (shouldStop) {
        clearInterval(pollInterval);
      }
    }, 5000); // Poll every 5 seconds
    
    return () => clearInterval(pollInterval);
  }, [currentVideoId, isGenerating, pollVideoStatus]);

  const handleGenerate = async (prompt, duration, sourceImage = null, quality = 'standard') => {
    setIsGenerating(true);
    setGenerationStatus('starting');

    try {
      const requestData = { prompt, duration, quality };
      
      // If we have a source image, include it in the request
      if (sourceImage) {
        // Extract base64 data from data URL if needed
        const base64Data = sourceImage.includes('base64,') 
          ? sourceImage.split('base64,')[1] 
          : sourceImage;
        requestData.source_image = base64Data;
      }
      
      const response = await api.post('/api/videos/generate', requestData);

      const newVideo = {
        id: response.data.id,
        prompt: response.data.prompt,
        thumbnailUrl: null,
        videoUrl: null,
        duration: response.data.duration,
        quality: response.data.quality,
        modelUsed: response.data.model_used,
        createdAt: response.data.created_at,
        status: 'processing'
      };

      setVideos(prev => [newVideo, ...prev]);
      setCurrentVideoId(response.data.id);
      setGenerationStatus('processing');
      
    } catch (error) {
      console.error('Failed to generate video:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to generate video. Please try again.';
      alert(errorMessage);
      setIsGenerating(false);
      setGenerationStatus(null);
    }
  };

  return (
    <VideoGenerator
      videos={videos}
      onGenerate={handleGenerate}
      isGenerating={isGenerating}
      generationStatus={generationStatus}
      fromImage={fromImage}
      sourceImageUrl={sourceImageUrl}
      suggestedPrompt={suggestedPrompt}
    />
  );
};

export default VideoPage;
